import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Box, Typography } from '@mui/material'
import CustomCard from "../CustomCardComponent/CustomCard";
import GroupAffiliationSelectionModal from "../Reservation/GroupAffiliationSelectionModal";
import { Button} from "../Button";
import { GPO_MESSAGES } from '../../constants';
import { fetchEligibleContractsApi } from "@/utils/PricingService/PricingApi";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { AlertMessage } from "../AlertMessage";

const GPOComponent = ({ productsForPricing, onContractChange, setSelectedContract, selectedContract }) => {
    const isOverlappingContractEnabled = useFeatureFlag("IS_OVERLAPPING_CONTRACT_ENABLED");
    const userData = useSelector((store) => store?.commerce?.userData);
    const cart = useSelector((store) => store?.commerce?.cart?.cart) || '';
    const { priceContracts = []} = useSelector((state) => state?.commerce);
    const selectedGpoAffiliatedId = (isOverlappingContractEnabled ? selectedContract?.id : userData?.selectedContract?.id) || '';
    const [openSelectionModal, setOpenSelectionModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');

    const userId = useSelector((store) => store?.commerce?.userData?.userId) || '';
    const isGPOSelected = () => {return !!userData?.selectedContract?.id}
    const dispatch = useDispatch();
    const alertRef = useRef(null);

    const alert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
  }

    const handleConfirm = (selectedPriceContract) => {
        onContractChange(selectedPriceContract);
    };

    const fetchEligibleContracts = async () => {
        try {
            await dispatch(fetchEligibleContractsApi({ payload: { productNumbers: productsForPricing, userId: userId }})).unwrap();
        } catch (error) {
            let errorMsg = 'We are unable to fetch your contracts at the moment. Please refresh and try again or contact us at 1-866-MODERNA.';
            alert(errorMsg, 'error');
            alertRef.current?.openAlert(error);
        }
    };
    
    const getSelectedContractDetails = (cartContractId) => {
        return priceContracts?.find(contract => contract?.id === cartContractId);
    }

    useEffect(() => {
        isOverlappingContractEnabled && fetchEligibleContracts();
    }, [dispatch]);

    useEffect(() => {
        if (
            isOverlappingContractEnabled &&
            cart?.contractInfo &&
            priceContracts.length > 0
        ) {
            const matchedContract = getSelectedContractDetails(cart.contractInfo.contractId);
            
            if (matchedContract) {
            setSelectedContract && setSelectedContract(matchedContract);
            }
        }
    }, [cart?.contractInfo, priceContracts]);

    useEffect(() => {
        if (
            isOverlappingContractEnabled &&
            cart?.cartStatus === "CREATED" &&
            priceContracts?.length > 0
        ) {
            setOpenSelectionModal(true);
        }
    }, [priceContracts]);

    const renderContractAffiliationCard = () => {
        if (isOverlappingContractEnabled) {
            return (
                <>
                    <AlertMessage variant={"filled"} type={alertType} message={alertMessage} sx={{ top: 120 }} ref={alertRef} />
                    {!priceContracts.length ? (
                        <CustomCard sx={{padding: "16px 32px"}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                                <Box display="flex" alignItems="center">
                                    <Typography variant='h5' style={{ fontSize: '18px', marginRight: '8px' }}>
                                        You are not affiliated with any contracts.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography>
                                        Incorrect pricing?
                                        <Link to={"/contact-us"} style={{ color: "#0379B2" }}> Contact us</Link>
                                    </Typography>
                                </Box>
                            </Box>
                        </CustomCard>
                    )   :   (
                        <CustomCard sx={{padding: "16px 32px"}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                                <Box display="flex" alignItems="center">
                                    <Typography variant='h5' style={{ fontSize: '18px', marginRight: '8px' }}>
                                        {selectedContract?.displayName ? 'Contract affiliation:' + '\u00A0' + selectedContract?.displayName : 'No contract affiliation selected.'}
                                        </Typography>
                                    <Typography
                                        variant="body1"
                                        color='secondary.textLinks'
                                        data-testid="selectAffiliationBtn"
                                        onClick={() => setOpenSelectionModal(true)}
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '18px'
                                        }}
                                    >
                                        Change
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography>
                                        Incorrect pricing?
                                        <Link to={"/contact-us"} style={{ color: "#0379B2" }}> Contact us</Link>
                                    </Typography>
                                </Box>
                            </Box>
                        </CustomCard>
                    )}
                </>
            ) 
        } else {
            return !isGPOSelected() ? (
                <CustomCard
                    title={'Contract affiliation'}
                    sx={{padding: "16px 32px", display: 'flex', flexDirection: 'column'}}
                >
                    <Typography fontFamily={'Aeonik Regular'} mt={1}>{GPO_MESSAGES.GPO_MISSING_LINE_1}</Typography>
                    <Typography fontFamily={'Aeonik Regular'} mb={2}>{GPO_MESSAGES.GPO_MISSING_LINE_2}</Typography>
                    <Button
                        buttonType="mds-primary"
                        data-testid="selectAffiliationBtn"
                        sx={{ width: "260px", gap: "12px",
                            fontSize: "14px", fontWeight: 700,
                            textTransform:"none",
                            padding: "16px 24px"
                        }}
                        onClick={() => setOpenSelectionModal(true)}>
                        Select a contract affiliation
                    </Button>
                </CustomCard>
            )   :   (
                <CustomCard
                    sx={{padding: "16px 32px"}}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                        <Box display="flex" alignItems="center">
                            <Typography variant='h5' style={{ fontSize: '18px', marginRight: '8px' }}>
                                {'Contract affiliation:' + '\u00A0' + (userData?.selectedContract?.displayName || '')}
                            </Typography>
                            <Typography
                                variant="body1"
                                color='secondary.textLinks'
                                data-testid="selectAffiliationBtn"
                                onClick={() => setOpenSelectionModal(true)}
                                style={{
                                    cursor: 'pointer',
                                    fontSize: '18px'
                                }}
                            >
                                Change
                            </Typography>
                        </Box>
                        <Box>
                            <Typography>
                                Incorrect pricing?
                                <Link to={"/contact-us"} style={{ color: "#0379B2" }}> Contact us</Link>
                            </Typography>
                        </Box>
                    </Box>
                </CustomCard>
            )
        }
    }

    return (
        <Box display="flex" flexDirection="column" mt={2} minHeight={'50px'} data-testid="gpoComponent">
            {renderContractAffiliationCard()}
            { openSelectionModal &&
                <GroupAffiliationSelectionModal
                    openModal={openSelectionModal}
                    closeModal={()=> setOpenSelectionModal(false)}
                    handleConfirm={ handleConfirm }
                    defaultGpoAffiliatedId={selectedGpoAffiliatedId}
                    productsForPricing={productsForPricing}
                    setSelectedContract={setSelectedContract}
                />
            }
        </Box>
    )
}

export default GPOComponent
